import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ReportShell, { useReportDates } from '../../components/reports/ReportShell';
import {
  BusyHeatmap,
  ChartCard,
  KpiCard,
  SimpleBar,
  SimpleLine,
  SimplePie,
  dictToPie,
  formatDurationSeconds,
} from '../../components/reports/ReportCharts';
import { reportsApi } from '../../services/reportsApi';
import { downloadCsv } from '../../components/reports/reportExport';
import { useKeepGrantedTab } from '../../brand/MenuPolicyContext';
import { CENTRAL_REPORT_TAB_IDS, hiddenTabProps } from '../../brand/menuCatalog';

const CentralReportsPage: React.FC = () => {
  const { dateFrom, dateTo, setDateFrom, setDateTo } = useReportDates(30);
  const [tab, setTab] = useState(0);
  const reportTabOn = useKeepGrantedTab(tab, CENTRAL_REPORT_TAB_IDS, setTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pools, setPools] = useState<{ id: number; code: string; name: string }[]>([]);
  const [poolId, setPoolId] = useState<number | ''>('');
  const [overview, setOverview] = useState<any>(null);
  const [events, setEvents] = useState<any>(null);
  const [alerts, setAlerts] = useState<any>(null);
  const [fp, setFp] = useState<any>(null);
  const [busy, setBusy] = useState<any>(null);
  const [eventList, setEventList] = useState<any>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await reportsApi.ping();
    } catch (e) {
      // ping 실패해도 개별 API 시도 — 인증·일시 오류를 백엔드 재시작으로 오인하지 않음
      console.warn('[reports] ping skipped', e);
    }

    const period = {
      date_from: dateFrom,
      date_to: dateTo,
      ...(poolId === '' ? {} : { pool_id: Number(poolId) }),
    };
    const settled = await Promise.allSettled([
      reportsApi.centralOverview({ date_from: dateFrom, date_to: dateTo }),
      reportsApi.centralEvents(period),
      reportsApi.centralAlerts({ date_from: dateFrom, date_to: dateTo }),
      reportsApi.centralFalsePositives(period),
      reportsApi.centralBusyHours(period),
      reportsApi.pools(),
      reportsApi.centralEventList({ ...period, limit: 200 }),
    ]);

    const labels = ['운영요약', '이벤트집계', '알림', '오탐', '혼잡', '시설목록', '이벤트내역'];
    const fails: string[] = [];
    settled.forEach((r, i) => {
      if (r.status === 'rejected') {
        const msg = (r.reason as Error)?.message || String(r.reason);
        fails.push(`${labels[i]}(${msg})`);
        console.error(`[reports] ${labels[i]} 실패`, r.reason);
      }
    });

    if (settled[0].status === 'fulfilled') setOverview(settled[0].value);
    else setOverview(null);
    if (settled[1].status === 'fulfilled') setEvents(settled[1].value);
    if (settled[2].status === 'fulfilled') setAlerts(settled[2].value);
    if (settled[3].status === 'fulfilled') setFp(settled[3].value);
    if (settled[4].status === 'fulfilled') setBusy(settled[4].value);
    if (settled[5].status === 'fulfilled') {
      setPools(settled[5].value?.pools || []);
    }
    if (settled[6].status === 'fulfilled') setEventList(settled[6].value);
    else setEventList(null);

    if (fails.length === labels.length) {
      setError(null);
      console.warn('[reports] all endpoints failed', fails);
    } else if (fails.length) {
      // 일부만 실패 — 화면은 채우고 콘솔만 (붉은 오류로 진입 방해하지 않음)
      console.warn('[reports] partial failures', fails);
      setError(null);
    }
    setLoading(false);
  }, [dateFrom, dateTo, poolId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCsv = () => {
    if (tab === 0 && overview) {
      downloadCsv('central_overview.csv', [
        ['metric', 'value'],
        ...Object.entries(overview.inventory || {}).map(([k, v]) => [k, v as any]),
        ['events_total', overview.events?.total],
        ['false_positive', overview.events?.false_positive_count],
        ['alert_success_rate', overview.alerts?.success_rate],
      ]);
    } else if (tab === 1 && eventList?.items) {
      downloadCsv('central_event_list.csv', [
        ['created_at', 'pool', 'type', 'risk', 'status', 'location', 'message'],
        ...eventList.items.map((r: any) => [
          r.created_at,
          r.pool_name,
          r.event_type,
          r.risk_level,
          r.status,
          r.location,
          r.message,
        ]),
      ]);
    } else if (tab === 2 && events) {
      downloadCsv('central_events_by_hour.csv', [
        ['hour', 'count'],
        ...(events.by_hour || []).map((r: any) => [r.hour, r.count]),
      ]);
    } else if (tab === 3 && alerts) {
      downloadCsv('central_alerts.csv', [
        ['channel', 'success', 'failed'],
        ...(alerts.by_channel || []).map((r: any) => [r.channel, r.success, r.failed]),
      ]);
    } else if (tab === 4 && fp) {
      downloadCsv('false_positives.csv', [
        ['pool', 'count'],
        ...(fp.by_pool || []).map((r: any) => [r.pool_name, r.count]),
      ]);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <ReportShell
        title="중앙 운영 리포트"
        printRootId="central-report-print"
        loading={loading}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        onRefresh={load}
        onCsv={onCsv}
        extraFilters={
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>시설</InputLabel>
            <Select
              label="시설"
              value={poolId}
              onChange={(e) => setPoolId(e.target.value as number | '')}
            >
              <MenuItem value="">전체</MenuItem>
              {pools.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        }
      >
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !overview && !error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            집계 데이터 없음. 백엔드를 재시작한 뒤 「조회」를 눌러.
          </Alert>
        )}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} className="no-print" variant="scrollable">
          <Tab label="운영 요약" {...hiddenTabProps(reportTabOn(0))} />
          <Tab label="이벤트 발생 내역" {...hiddenTabProps(reportTabOn(1))} />
          <Tab label="이벤트·위험 집계" {...hiddenTabProps(reportTabOn(2))} />
          <Tab label="알림 발송" {...hiddenTabProps(reportTabOn(3))} />
          <Tab label="오탐" {...hiddenTabProps(reportTabOn(4))} />
          <Tab label="혼잡 시간" {...hiddenTabProps(reportTabOn(5))} />
        </Tabs>

        {tab === 0 && overview && (
          <Box>
            {(overview.events?.total ?? 0) === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                선택 기간 내 DB 이벤트 없음. 아래 재고 수치는 현재 기준. 기간을 넓힌 뒤 「조회」를 실행.
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard
                  label="시설"
                  value={`${overview.inventory?.pools_active ?? 0} / ${overview.inventory?.pools_total ?? 0}`}
                  hint="활성 / 전체"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard label="CCTV" value={overview.inventory?.cctv_total ?? 0} />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard label="회원" value={overview.inventory?.members_active ?? 0} />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard
                  label="웨어러블"
                  value={`${overview.inventory?.wearables_active ?? 0} / ${overview.inventory?.wearables_total ?? 0}`}
                  hint="활성 / 전체"
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard label="기간 이벤트" value={overview.events?.total ?? 0} />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard label="오탐" value={overview.events?.false_positive_count ?? 0} />
              </Grid>
              <Grid item xs={6} sm={4} md={3} lg>
                <KpiCard
                  label="알림 성공률"
                  value={`${overview.alerts?.success_rate ?? 0}%`}
                  hint={`${overview.alerts?.success ?? 0}/${overview.alerts?.total ?? 0}`}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="일별 이벤트"
                  empty={!(overview.events?.by_day || []).length}
                >
                  <SimpleLine data={overview.events?.by_day || []} xKey="date" yKey="count" />
                </ChartCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="위험 수준"
                  empty={!dictToPie(overview.events?.by_risk_level || {}).length}
                >
                  <SimplePie data={dictToPie(overview.events?.by_risk_level || {})} />
                </ChartCard>
              </Grid>
              <Grid item xs={12}>
                <ChartCard
                  title="시설별 이벤트"
                  height={320}
                  empty={!(overview.events?.by_pool || []).length}
                >
                  <SimpleBar
                    data={overview.events?.by_pool || []}
                    xKey="pool_name"
                    yKey="count"
                  />
                </ChartCard>
              </Grid>
            </Grid>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              이벤트 발생 내역 (기간 내 {eventList?.total ?? overview?.recent_events?.length ?? 0}건)
            </Typography>
            {!(eventList?.items || overview?.recent_events || []).length ? (
              <Alert severity="info">
                선택 기간에 이벤트 없음. 대시보드「최근 이벤트」에 보이는 건과 기간을 맞춰 「조회」.
                (DB 확인 시 motion_risk 등이 events 테이블에 있어야 함.)
              </Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>시각</TableCell>
                    <TableCell>시설</TableCell>
                    <TableCell>유형</TableCell>
                    <TableCell>위험</TableCell>
                    <TableCell>상태</TableCell>
                    <TableCell>위치</TableCell>
                    <TableCell>메시지</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(eventList?.items || overview?.recent_events || []).map((row: any) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {(row.created_at || '').replace('T', ' ').slice(0, 19)}
                      </TableCell>
                      <TableCell>{row.pool_name || row.pool_id}</TableCell>
                      <TableCell>{row.event_type}</TableCell>
                      <TableCell>{row.risk_level}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.location || '-'}</TableCell>
                      <TableCell>{row.message || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {tab === 2 && events && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ChartCard
                title="상태별"
                empty={!dictToPie(events.by_status || {}).length}
              >
                <SimplePie data={dictToPie(events.by_status || {})} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ChartCard
                title="해결 유형"
                empty={!dictToPie(events.by_resolve_type || {}).length}
              >
                <SimplePie data={dictToPie(events.by_resolve_type || {})} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ChartCard
                title={`평균 해결시간 · ${formatDurationSeconds(events.mttr_seconds_avg)}`}
                empty={!(events.mttr_histogram || []).some((b: any) => b.count > 0)}
                emptyText={
                  events.mttr_sample_count
                    ? '표시할 분포 데이터 없음'
                    : '해결된 이벤트 없음'
                }
              >
                <SimpleBar
                  data={events.mttr_histogram || []}
                  xKey="bucket"
                  yKey="count"
                  color="#6a1b9a"
                />
              </ChartCard>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
                표본 {events.mttr_sample_count ?? 0}건 · 구간별 해결 건수
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ChartCard
                title="시간대별 이벤트"
                empty={!(events.by_hour || []).some((r: any) => (r.count || 0) > 0)}
              >
                <SimpleBar data={events.by_hour || []} xKey="hour" yKey="count" color="#ed6c02" />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {tab === 3 && alerts && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ChartCard title="채널별 성공/실패" height={300}>
                <SimpleBar
                  data={(alerts.by_channel || []).map((r: any) => ({
                    name: r.channel,
                    success: r.success,
                    failed: r.failed,
                  }))}
                  xKey="name"
                  yKey="success"
                  color="#2e7d32"
                />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard title="일별 발송">
                <SimpleLine data={alerts.by_day || []} xKey="date" yKey="count" color="#6a1b9a" />
              </ChartCard>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                시설별 발송
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>시설</TableCell>
                    <TableCell align="right">건수</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(alerts.by_pool || []).map((r: any) => (
                    <TableRow key={r.pool_id}>
                      <TableCell>{r.pool_name}</TableCell>
                      <TableCell align="right">{r.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          </Grid>
        )}

        {tab === 4 && fp && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <KpiCard label="오탐 합계" value={fp.total ?? 0} />
            </Grid>
            <Grid item xs={12} md={8}>
              <ChartCard title="일별 오탐">
                <SimpleLine data={fp.by_day || []} xKey="date" yKey="count" color="#d32f2f" />
              </ChartCard>
            </Grid>
            <Grid item xs={12}>
              <ChartCard title="시설별 오탐">
                <SimpleBar data={fp.by_pool || []} xKey="pool_name" yKey="count" color="#d32f2f" />
              </ChartCard>
            </Grid>
          </Grid>
        )}

        {tab === 5 && busy && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              요일×시간 이벤트 히트맵
            </Typography>
            <BusyHeatmap labels={busy.weekday_labels || []} grid={busy.grid || []} />
            <Box sx={{ mt: 2 }}>
              <ChartCard title="시간대 합계">
                <SimpleBar data={busy.by_hour || []} xKey="hour" yKey="count" />
              </ChartCard>
            </Box>
          </Box>
        )}
      </ReportShell>
    </Box>
  );
};

export default CentralReportsPage;
